// homeHiddenPersist.js — the one decision behind "which entries of the home
// board's hidden map are DURABLE?"
//
// The composed map the board renders (`_composeHiddenMap` in pages/main.js) is
// three different kinds of fact stacked together:
//
//   1. DEFAULTS      — `_DEFAULT_HIDDEN_WIDGETS`, the workspace panel set, the
//                      installed apps' declared widget defaults. Re-derived on
//                      every boot from data the shell already holds.
//   2. USER CHOICES  — the hides and restores a person actually performed.
//   3. RENDER CLAMPS — a house row whose route gate is OFF, a module widget the
//                      panel set does not grant. Policy, evaluated per render.
//
// Only (2) is durable. The write path did not know that: `_hideWidgetById`
// persisted `{ ...root._homeHiddenWidgets, [id]: true }`, so ONE hide wrote all
// three kinds to the localStorage cache AND the server row. Measured on the
// app-mode vault workspace (2026-09-10): hiding a single app icon persisted 27
// keys — 16 pure defaults (`received … app-infra`), 5 app-plane defaults, 5
// redundant `false`s, and the one real gesture.
//
// Three costs, in rising order of seriousness:
//   • the row stops being readable — nobody can tell a choice from a default;
//   • a default can never be CHANGED again for anyone who has ever touched
//     their board: the stale copy in their row outranks the new default
//     (`{ ...baseHidden, ...savedHidden }`), which is the same "frozen the
//     async-mount race into the row as fake user hides" failure the first-run
//     seed already carries a warning about;
//   • a RENDER CLAMP written into the row is a policy decision turned into a
//     user preference — it outlives the policy. A house row hidden because its
//     route gate was off stays hidden in the row after the gate comes back on.
//
// Pure by design: the caller supplies every input, so a test can pin the
// decision without a browser, a grid, or a network.

/**
 * @param {object} args
 * @param {object} args.stored    the map currently persisted (server row ▸ cache)
 * @param {object} args.gestures  the EXPLICIT choices made in this document.
 *   Every entry here is written VERBATIM — a gesture is never pruned against
 *   `base`, because `base` is still converging while the board is clickable.
 * @param {object} args.base      the default map for this workspace RIGHT NOW
 * @param {Set<string>} args.knownIds  every widget id this workspace can render
 * @param {Set<string>} args.gatedIds  ids currently clamped hidden by a gate
 * @returns {object} the map to write — explicit choices only
 */
export const persistableHiddenMap = ({ stored, gestures, base, knownIds, gatedIds } = {}) => {
  const _stored = stored && typeof stored === 'object' ? stored : {}
  const _gestures = gestures && typeof gestures === 'object' ? gestures : {}
  const _base = base && typeof base === 'object' ? base : {}
  const known = knownIds instanceof Set ? knownIds : new Set(knownIds || [])
  const gated = gatedIds instanceof Set ? gatedIds : new Set(gatedIds || [])

  const out = {}
  for (const id of new Set([...Object.keys(_stored), ...Object.keys(_gestures)])) {
    // A clamp is never a stored choice — not even when the user's own last
    // gesture agreed with it. The gate decides again on every render.
    if (gated.has(id)) continue
    // A key with no catalog row HERE cannot be acted on here: the drawer will
    // not offer it and no card can carry it. Keeping it only lets one
    // workspace's vocabulary accumulate in another's row.
    if (!known.has(id)) continue
    const isGesture = Object.prototype.hasOwnProperty.call(_gestures, id)
    const value = isGesture ? !!_gestures[id] : !!_stored[id]
    // An INHERITED entry that agrees with the current default carries no
    // information: the read path composes `{ ...base, ...saved }`, so dropping
    // it leaves today's outcome identical and lets tomorrow's default reach the
    // user. This is what makes the pruning safe to apply to entries written
    // long before this rule existed — it can only ever remove a redundancy.
    //
    // A GESTURE IS NEVER PRUNED, even when it happens to agree with today's
    // base (WS-HOME-PREFS-PERSIST-ALWAYS-1). Two reasons, and the first is a
    // measured data-loss class, not a preference:
    //
    //   1. `base` is still CONVERGING while the board is already clickable.
    //      It is re-derived per call from live root state — the workspace's
    //      panel set, the installed apps' manifests, and (since
    //      WS-HOME-APPMODE-DEFAULT-WIDGETS-1) whether this workspace is an
    //      app-mode install, a fact that arrives with `workspaceSettings`
    //      seconds after the first paint. So the same click can be durable or
    //      silently discarded depending only on WHEN it happened. Durability
    //      must never depend on a value that is still settling.
    //   2. A default and a decision are different kinds of fact. "This is off
    //      unless you say otherwise" is a default; "I turned this off" is a
    //      person's answer, and it must keep outranking a default that later
    //      changes its mind. Storing it is the whole point.
    //
    // The fossilised-defaults defect WS-HOME-PREFS-DURABLE-1 fixed is untouched
    // by this: only the ledger's entries are exempt, and the ledger is recorded
    // at gesture sites ALONE (never by a resolver's reconcile) — see the
    // `_recordUserHidden` contract in pages/main.js.
    if (!isGesture && value === !!_base[id]) continue
    out[id] = value
  }
  return out
}
